import React, { lazy, Suspense, useState } from "react";
import { useSetting } from "./SettingsStore.tsx";
import { parseRoster } from "../ClassSet.ts";
import { fluencyChartProps } from "../fluencyChartProps.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";

// Behind the lazy boundary like every other PDF surface: the renderer loads only if the visitor
// asks for a chart (bundleBoundary.test.js).
const FluencyChartPanel = lazy(() => import("../FluencyChartPanel.tsx"));

/**
 * The tracking chart's place in the rail, under the class roster (ADR 0071 §8): a heading and a
 * button, shown only while the sheet is a level sheet. It sits here and not with the level
 * settings because the roster is component state in `MathFactsGenerator` and must stay there
 * (ADR 0071 §6) - this takes it as a string and reads the names off it, so the settings context
 * never sees a name.
 *
 * Nothing is built until the button is pressed, and the chart it builds is a snapshot: change the
 * roster or the ladder and press again to get the new one.
 */
export function FluencyChartSection({ roster }: { roster: string }) {
    // The chart minus its names, as a slice (ADR 0081): it changes with the ladder and the
    // sheet's language and with nothing else, so a tap elsewhere does not redraw this.
    const base = useSetting((settings) => fluencyChartProps(settings, []));
    const t = useTranslation();
    const [requested, setRequested] = useState<string | null>(null);

    if (!base) {
        return null;
    }
    const chart = { ...base, names: parseRoster(roster).names };
    // The snapshot is the JSON the button was pressed on, so the chart does not rebuild with every
    // keystroke in the roster box.
    const shown = requested === null ? null : (JSON.parse(requested) as typeof chart);

    return (
        <section className="fluencyChartSection" aria-labelledby="fluencyChartHeading">
            <h3 id="fluencyChartHeading" className="fluencyChartHeading">{t("settings.fluencyChartTitle")}</h3>
            <p className="classRosterHint">{t("settings.fluencyChartHint")}</p>
            <button
                type="button"
                className="btn btn-block"
                onClick={() => setRequested(JSON.stringify(chart))}
            >
                {t("settings.makeFluencyChart")}
            </button>
            {shown && (
                <Suspense fallback={<div className="pdfLoading">{t("settings.buildingFluencyChart")}</div>}>
                    <FluencyChartPanel chart={shown} />
                </Suspense>
            )}
        </section>
    );
}

export default FluencyChartSection;
